import { Modal } from 'antd';
import { PureComponent, ReactNode } from 'react';
import { PatientHealthSummaryResponse } from '../../../api/getPatientHealthSummary';
import { FeedbackType, sendFeedbackToSlack } from './Utils';
import Tag from './Tag';
import BetaTag from './BetaTag';
import FeedbackPopover from './FeedbackPopover';

interface Props {
    isVisible?: boolean;
    closeMe: () => void;
    patientId: string;
    patientName: string;
    patientHealthSummary: PatientHealthSummaryResponse;
    from: string;
}

class AllInsightsModalVisible extends PureComponent<Props> {
    onFeedback = (type: FeedbackType, input?: string): void => {
        const { patientId, from } = this.props;
        sendFeedbackToSlack(type, patientId, input || '', from);
    };

    render(): ReactNode {
        const {
            isVisible,
            closeMe,
            patientName,
            patientHealthSummary,
            from,
            patientId,
        } = this.props;
        const { onFeedback } = this;

        return (
            <Modal
                bodyStyle={{
                    padding: '0px',
                    borderRadius: '8px !important',
                    height: '575px',
                }}
                visible={isVisible}
                width={700}
                onCancel={(e) => {
                    e.stopPropagation();
                    closeMe();
                }}
                footer={null}
                closable={false}
                destroyOnClose
                style={{ top: 50, borderRadius: '8px !important', height: '575px' }}
                className="overflow-hidden rounded-8"
            >
                <div
                    className="h-full overflow-hidden flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between px-16 py-12">
                        <div className="flex items-center space-x-8">
                            <img
                                src="/images/health-summary-icon.png"
                                alt="health summary icon"
                                className="w-24 h-24"
                            />
                            <p className="text-20">AI health summary of {patientName}</p>
                            <BetaTag />
                        </div>
                        <p
                            className="cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                closeMe();
                            }}
                        >
                            <svg
                                className="w-12 h-12"
                                viewBox="0 0 10 9"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M8.88477 1.60352L5.83789 4.65039L8.85938 7.67188C9.11328 7.90039 9.11328 8.28125 8.85938 8.50977C8.63086 8.76367 8.25 8.76367 8.02148 8.50977L4.97461 5.48828L1.95312 8.50977C1.72461 8.76367 1.34375 8.76367 1.11523 8.50977C0.861328 8.28125 0.861328 7.90039 1.11523 7.64648L4.13672 4.625L1.11523 1.60352C0.861328 1.375 0.861328 0.994141 1.11523 0.740234C1.34375 0.511719 1.72461 0.511719 1.97852 0.740234L5 3.78711L8.02148 0.765625C8.25 0.511719 8.63086 0.511719 8.88477 0.765625C9.11328 0.994141 9.11328 1.375 8.88477 1.60352Z"
                                    fill="#1A1A1A"
                                />
                            </svg>
                        </p>
                    </div>
                    <div className="flex-1 overflow-hidden flex items-start border-t border-b border-darwin-neutral_new-200">
                        <div className="overflow-auto h-full w-60p space-y-20 py-16 pl-16 pr-8">
                            <div className="space-y-20">
                                <p className="text-16 font-700" style={{ color: '#1A1A1A' }}>
                                    Medical profile
                                </p>
                                <div className="flex items-center gap-8 flex-wrap">
                                    {patientHealthSummary?.tags?.map(({ tag, color_scheme }) => (
                                        <Tag content={tag} type={color_scheme} />
                                    ))}
                                </div>
                            </div>
                            {patientHealthSummary?.medical_profile?.map(({ heading, insight }) => (
                                <div className="space-y-4">
                                    <p className="font-700" style={{ color: '#1A1A1A' }}>
                                        {heading}
                                    </p>
                                    <p className="text-13 text-darwin-neutral_new-800">{insight}</p>
                                </div>
                            ))}
                        </div>
                        <div className="overflow-auto h-full w-40p space-y-16 py-16 pl-8 pr-16">
                            <div className="p-16 bg-darwin-neutrals-50 rounded-8 space-y-12">
                                <p className="text-16 font-700" style={{ color: '#1A1A1A' }}>
                                    Recommended Lab tests
                                </p>
                                <div className="space-y-14">
                                    {patientHealthSummary?.suggested_lab_tests?.map(
                                        ({ test_name, reason }) => (
                                            <div className="text-13 space-y-4">
                                                <p className="font-700">{test_name}</p>
                                                <p>{reason}</p>
                                            </div>
                                        ),
                                    )}
                                </div>
                            </div>
                            {patientHealthSummary?.updates_in_last_visit &&
                                patientHealthSummary.updates_in_last_visit.length > 0 && (
                                    <div className="p-16 bg-darwin-neutrals-50 rounded-8 space-y-12">
                                        <p
                                            className="text-16 font-700"
                                            style={{ color: '#1A1A1A' }}
                                        >
                                            Updates from last visit
                                        </p>
                                        <div className="text-13 space-y-14">
                                            {patientHealthSummary?.updates_in_last_visit?.map(
                                                (i) => (
                                                    <p>{i}</p>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                )}
                        </div>
                    </div>
                    <div className="flex items-center justify-center space-x-8 px-16 py-12">
                        <div className="text-12 text-darwin-accent-bright-blue-800 flex items-center whitespace-nowrap">
                            <button
                                className="flex items-center px-8 py-6 space-x-4"
                                onClick={() => onFeedback('positive')}
                            >
                                <span>This was helpful</span>
                                <span>
                                    <svg
                                        className="w-14 h-12 fill-darwin-accent-bright-blue-800"
                                        viewBox="0 0 14 12"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M8.70117 0.513672C9.69141 0.792969 10.25 1.80859 9.9707 2.77344L9.81836 3.28125C9.74219 3.56055 9.64062 3.83984 9.51367 4.09375H11.875C12.7637 4.09375 13.5 4.83008 13.5 5.71875C13.5 6.17578 13.2969 6.58203 13.0176 6.88672C13.0684 7.03906 13.0938 7.19141 13.0938 7.36914C13.0938 7.87695 12.8398 8.35938 12.4336 8.66406C12.459 8.76562 12.4844 8.86719 12.4844 8.99414C12.4844 9.50195 12.2305 9.95898 11.875 10.2637C11.8242 11.127 11.1133 11.8379 10.25 11.8379H7.76172C7.20312 11.8379 6.64453 11.6602 6.1875 11.3555L4.61328 10.3145C4.33398 10.1113 4.25781 9.73047 4.46094 9.45117C4.63867 9.17188 5.01953 9.0957 5.29883 9.29883L6.87305 10.3398C7.12695 10.5176 7.43164 10.5938 7.76172 10.5938H10.25C10.4531 10.5938 10.6562 10.416 10.6562 10.1875C10.6562 10.1621 10.6309 10.1113 10.6309 10.0859C10.5547 9.78125 10.707 9.47656 10.9863 9.34961C11.1387 9.29883 11.2656 9.14648 11.2656 8.96875C11.2656 8.89258 11.2148 8.79102 11.1641 8.74023C11.0371 8.5625 11.0117 8.33398 11.0879 8.13086C11.1387 7.92773 11.3164 7.80078 11.5449 7.75C11.7227 7.72461 11.875 7.54688 11.875 7.34375C11.875 7.24219 11.8242 7.16602 11.7734 7.08984C11.6211 6.9375 11.5703 6.73438 11.6211 6.53125C11.6719 6.32812 11.8242 6.17578 12.002 6.09961C12.1543 6.04883 12.2812 5.89648 12.2812 5.71875C12.2812 5.51562 12.0781 5.3125 11.875 5.3125H8.47266C8.24414 5.3125 8.04102 5.18555 7.93945 4.98242C7.83789 4.7793 7.83789 4.55078 7.96484 4.37305C8.26953 3.94141 8.49805 3.45898 8.65039 2.95117L8.80273 2.44336C8.87891 2.11328 8.70117 1.7832 8.37109 1.68164C8.04102 1.60547 7.71094 1.7832 7.60938 2.11328L7.48242 2.62109C7.33008 3.12891 7.07617 3.58594 6.7207 3.99219L5.40039 5.41406C5.17188 5.66797 4.79102 5.69336 4.53711 5.46484C4.30859 5.23633 4.2832 4.85547 4.51172 4.60156L5.80664 3.17969C6.03516 2.92578 6.21289 2.62109 6.31445 2.29102L6.44141 1.7832C6.7207 0.792969 7.73633 0.234375 8.70117 0.513672ZM1.3125 4.5H2.9375C3.36914 4.5 3.75 4.88086 3.75 5.3125V11C3.75 11.457 3.36914 11.8125 2.9375 11.8125H1.3125C0.855469 11.8125 0.5 11.457 0.5 11V5.3125C0.5 4.88086 0.855469 4.5 1.3125 4.5Z" />
                                    </svg>
                                </span>
                            </button>
                            <FeedbackPopover
                                onFeedback={onFeedback}
                                removePopupContainer
                                from={from}
                                patientId={patientId}
                            >
                                <button className="flex items-center px-8 py-6 space-x-4">
                                    <span>Not helpful</span>
                                    <span>
                                        <svg
                                            className="w-14 h-12 fill-darwin-accent-bright-blue-800"
                                            viewBox="0 0 14 13"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M8.70117 11.7617C7.73633 12.041 6.7207 11.457 6.44141 10.4922L6.31445 9.98438C6.21289 9.6543 6.03516 9.34961 5.80664 9.0957L4.51172 7.67383C4.2832 7.41992 4.30859 7.03906 4.53711 6.81055C4.79102 6.58203 5.17188 6.60742 5.40039 6.86133L6.7207 8.2832C7.07617 8.66406 7.33008 9.14648 7.48242 9.6543L7.60938 10.1621C7.71094 10.4922 8.04102 10.6699 8.37109 10.5938C8.70117 10.4922 8.87891 10.1621 8.80273 9.83203L8.65039 9.32422C8.49805 8.81641 8.26953 8.33398 7.96484 7.90234C7.83789 7.72461 7.83789 7.4707 7.93945 7.26758C8.04102 7.06445 8.24414 6.9375 8.47266 6.9375H11.875C12.0781 6.9375 12.2812 6.75977 12.2812 6.53125C12.2812 6.37891 12.1543 6.22656 12.002 6.15039C11.8242 6.09961 11.6719 5.92188 11.6211 5.74414C11.5703 5.54102 11.6211 5.33789 11.7734 5.18555C11.8242 5.10938 11.875 5.0332 11.875 4.90625C11.875 4.72852 11.7227 4.55078 11.5449 4.52539C11.3164 4.47461 11.1387 4.32227 11.0879 4.14453C11.0117 3.94141 11.0371 3.71289 11.1641 3.53516C11.2148 3.48438 11.2656 3.38281 11.2656 3.28125C11.2656 3.12891 11.1387 2.97656 10.9863 2.90039C10.707 2.79883 10.5547 2.49414 10.6309 2.18945C10.6309 2.13867 10.6562 2.11328 10.6562 2.0625C10.6562 1.85938 10.4531 1.65625 10.25 1.65625H7.76172C7.43164 1.65625 7.12695 1.75781 6.87305 1.93555L5.29883 2.97656C5.01953 3.17969 4.63867 3.10352 4.46094 2.79883C4.25781 2.51953 4.33398 2.16406 4.61328 1.96094L6.1875 0.919922C6.64453 0.615234 7.20312 0.4375 7.76172 0.4375H10.25C11.1133 0.4375 11.8242 1.14844 11.875 2.01172C12.2305 2.31641 12.4844 2.77344 12.4844 3.28125C12.4844 3.4082 12.459 3.50977 12.4336 3.61133C12.8398 3.91602 13.0938 4.37305 13.0938 4.90625C13.0938 5.08398 13.0684 5.23633 13.0176 5.38867C13.2969 5.69336 13.5 6.09961 13.5 6.53125C13.5 7.44531 12.7637 8.15625 11.875 8.15625H9.51367C9.64062 8.43555 9.74219 8.71484 9.81836 8.99414L9.9707 9.50195C10.25 10.4668 9.69141 11.4824 8.70117 11.7617ZM1.3125 9.375C0.855469 9.375 0.5 9.01953 0.5 8.5625V2.875C0.5 2.44336 0.855469 2.0625 1.3125 2.0625H2.9375C3.36914 2.0625 3.75 2.44336 3.75 2.875V8.5625C3.75 9.01953 3.36914 9.375 2.9375 9.375H1.3125Z" />
                                        </svg>
                                    </span>
                                </button>
                            </FeedbackPopover>
                        </div>
                    </div>
                </div>
            </Modal>
        );
    }
}

export default AllInsightsModalVisible;
